import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import { useKeptVersion } from 'api/keptVersion';
import 'twin.macro';
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';

import SaveStatus from 'components/save-status/SaveStatus';
import UndoRedo from 'components/undo-redo/UndoRedo';
import { useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { usePendingSave } from 'context/PendingSaveContext';
import useBeforeUnloadWarning from 'hooks/useBeforeUnloadWarning';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import {
  PreferencesWork,
  missingForPreferences,
  preferencesBody,
  preferencesPath,
  savePreferences,
} from 'api/preferencesAutoSave';
import AppPage from 'components/page/app-page/AppPage';
import ConfigForm from './ConfigForm';
import { useQuery } from 'hooks/sympl-query';
import { useMutation } from 'hooks/sympl-mutation';
import { GET_PREFERENCES_CONFIG } from 'graphql/vacancies/queries';
import { DELETE_VACANCY } from 'graphql/vacancies/mutations';
import useNavigationContext from 'hooks/context/nav-context';
import {
  GET_EXTERNAL_JOBS,
  GET_WAITING_CANDIDATES,
} from 'graphql/integrations/queries';
import { WaitingCandidates } from 'components/integrations/waitingCandidates';
import {
  LINK_VACANCY_TO_EXTERNAL_JOB,
  UNLINK_VACANCY_TO_EXTERNAL_JOB,
} from 'graphql/integrations/mutations';
import {
  PreferencesFormData,
  PreferencesConfiguration,
  PreferencesFormQuestionKey,
  DeleteVacancyPayload,
  handleUnlinkExternalJobFn,
} from './PreferencesConfigTypes';
import * as QUESTIONS from './questions';
import PreferencesIntegrations from './PreferencesIntegrations';
import { ToastTypes } from 'types/notificationTypes';
import { useToastNotifications } from 'hooks/notificationHooks';
import { Routes } from 'types/routeTypes';
import { ArrowRight } from '@phosphor-icons/react';
import usePermissionContext from 'hooks/context/permission-context';
import { PERMISSIONS } from 'context/PermissionContext';
import { Footnote } from 'components/typography/Typography';
import { PostHogFeature } from 'posthog-js/react';

const PreferencesConfig: React.FC = () => {
  const formMethods = useForm();
  const navigate = useNavigate();

  const { activeVacancy, unsetActiveVacancy } = useNavigationContext();
  const { addToast } = useToastNotifications();
  const { userHasPermission } = usePermissionContext();

  const [formKey, setFormKey] = useState(0);
  const [isDoneUpdating, setIsDoneUpdating] = useState(false);
  const [selectedExternalJob, setSelectedExternalJob] = useState<string>();
  const [formData, setFormData] = useState<PreferencesFormData[]>();
  /**
   * The campaign the form on screen was built for. Picking another campaign
   * does not rebuild this screen at once: for a moment the form still holds
   * the previous campaign's values, and they must not be stored on the new one.
   */
  const [loadedFor, setLoadedFor] = useState<number | null>(null);
  const [loads, setLoads] = useState(0);
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();
  const preferencesData = useMemo(() => {
    const allowedSections = new Set();
    if (userHasPermission(PERMISSIONS.MANAGE_CANDIDATES)) {
      allowedSections.add('Automated Candidate Mails').add('Notifications');
    }
    if (userHasPermission(PERMISSIONS.MANAGE_CAMPAIGNS)) {
      formData?.forEach((section) => {
        if (section.section !== 'Notifications') {
          allowedSections.add(section.section);
        }
      });
    }

    return formData?.filter((section) => allowedSections.has(section.section));
  }, [formData]);

  const isAllowedToCreateTemplates = userHasPermission(
    PERMISSIONS.MANAGE_CAMPAIGNS,
    'customer'
  );

  const {
    data: configData,
    loading: loadingConfig,
    refetch: refetchConfigData,
  } = useQuery<PreferencesConfiguration, { vacancyId: number }>(
    GET_PREFERENCES_CONFIG,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: !activeVacancy,
      variables: { vacancyId: activeVacancy ?? 0 },
    }
  );
  const {
    data: externalJobsData,
    loading: loadingExternalJobs,
    refetch: refetchExternalJobs,
  } = useQuery(GET_EXTERNAL_JOBS);

  const [linkExternalJob] = useMutation(LINK_VACANCY_TO_EXTERNAL_JOB);
  const [unlinkExternalJob] = useMutation(UNLINK_VACANCY_TO_EXTERNAL_JOB);

  const externalJobs = externalJobsData?.externalJobs ?? [];

  // SYM-6446: who a link to the picked job would send right away.
  const selectedIntegrationId: number | undefined = externalJobs.find(
    (j: any) => j.id === selectedExternalJob
  )?.integration_id;
  const { data: waitingData } = useQuery<
    { waitingCandidates: WaitingCandidates },
    { vacancyId: number; integrationId: number }
  >(GET_WAITING_CANDIDATES, {
    fetchPolicy: 'network-only',
    skip: !activeVacancy || !selectedIntegrationId,
    variables: {
      vacancyId: activeVacancy ?? 0,
      integrationId: selectedIntegrationId ?? 0,
    },
  });

  /**
   * Updates the form field value using a given section & question index.
   * @param sectionIndex The form section index
   * @param questionIndex The form question index
   * @param key The form question key
   * @param value The updated value
   */
  const formDataChangeHandler = (
    sectionIndex: number,
    questionIndex: number,
    _key: PreferencesFormQuestionKey,
    value: string | number | boolean
  ) => {
    // From the form as it is NOW, not as it was when this handler was made:
    // "Reset to default" answers a moment later, and written from the older
    // form it put back what the person had changed meanwhile (review of the
    // slice). New objects, not the old ones changed in place: an Undo step
    // holds the old ones.
    setFormData((current) =>
      current?.map((section, s) =>
        s !== sectionIndex
          ? section
          : {
              ...section,
              questions: section.questions.map((question, q) =>
                q !== questionIndex ? question : { ...question, value }
              ),
            }
      )
    );
  };

  const [deleteVacancy] = useMutation<undefined, DeleteVacancyPayload>(
    DELETE_VACANCY
  );

  /*
   * Auto-save. This screen stores itself, on every campaign: it has no
   * publish moment (D34), what is stored here is in use at once. So today's
   * rule stays: while a required field is empty NOTHING on the screen is
   * stored, the corner says why, and leaving says the screen goes back to
   * how it was last saved (D35).
   */
  const own = !!activeVacancy && loadedFor === activeVacancy;
  const work = useMemo<PreferencesWork | undefined>(() => {
    if (!own || !formData) return undefined;
    const valueOf = (key: PreferencesFormQuestionKey) =>
      formData.flatMap((s) => s.questions).find((q) => q.key === key)?.value;
    return {
      name: (valueOf('name') as string) ?? '',
      lead_recruiter: (valueOf('lead_recruiter') as number) ?? 0,
      hide_from_careers_page: !!valueOf('hide_from_careers_page'),
      email_on_candidate: !!valueOf('email_on_candidate'),
      email_on_communication: !!valueOf('email_on_communication'),
      vacancy_template_done_survey: valueOf('vacancy_template_done_survey') as
        | number
        | string
        | undefined,
      vacancy_template_ask_for_cv: valueOf('vacancy_template_ask_for_cv') as
        | number
        | string
        | undefined,
      vacancy_template_got_cv: valueOf('vacancy_template_got_cv') as
        | number
        | string
        | undefined,
    };
  }, [own, formData]);

  const missing = work ? missingForPreferences(work) : [];
  const incomplete = missing.length > 0;
  /*
   * What the server holds of this screen, as far as the screen knows: what
   * was loaded, then what was last stored. While a required field is empty
   * the saver is handed THIS, not the screen:
   * - it has nothing to store, and a store that was already waiting for its
   *   pause is called off (a box ticked just before the name was emptied
   *   went out anyway, with the old name; review of the slice);
   * - a campaign that OPENS with a required field empty still stores the
   *   first complete screen: the saver compares with what was loaded, not
   *   with the first complete screen it happens to see.
   */
  // D10: the count of stores this screen builds on.
  const version = useKeptVersion('preferences', activeVacancy ?? null);
  const storedWork = useRef<{ campaign: number; work: PreferencesWork }>();
  if (work && activeVacancy && storedWork.current?.campaign !== activeVacancy) {
    storedWork.current = { campaign: activeVacancy, work };
  }
  if (!own) storedWork.current = undefined;
  const storable = work
    ? incomplete
      ? storedWork.current?.work
      : work
    : undefined;
  /**
   * Held back: something is missing AND the person changed something. A
   * screen that merely opens with a field empty holds no unsaved work, so it
   * neither warns nor asks on leaving.
   */
  const heldBack =
    incomplete &&
    !!work &&
    JSON.stringify(work) !== JSON.stringify(storedWork.current?.work);
  const heldBackRef = useRef(heldBack);
  heldBackRef.current = heldBack;

  const storePreferences = useCallback(
    async (value: PreferencesWork, signal?: AbortSignal): Promise<string> => {
      if (!activeVacancy) throw new Error('No campaign is open.');
      const campaign = activeVacancy;
      await savePreferences(campaign, value, signal, {
        basedOn: version.basedOn(campaign),
        stored: (count) => version.stored(campaign, count),
      });
      if (storedWork.current?.campaign === activeVacancy) {
        storedWork.current = { campaign: activeVacancy, work: value };
      }
      return new Date().toISOString();
    },
    [activeVacancy]
  );

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<PreferencesWork>({
    value: storable,
    enabled: own && !!storable,
    // (While held back `storable` is what is already stored: nothing goes out.)
    identity: activeVacancy ? `preferences:${activeVacancy}` : null,
    save: storePreferences,
    flushOnHide: (value) => {
      if (activeVacancy) {
        sendKeepaliveSave(
          preferencesPath(activeVacancy),
          preferencesBody(value),
          'POST',
          version.basedOn(activeVacancy)
        );
      }
    },
    // The form on screen at a campaign change is the previous campaign's.
    flushOnIdentityChange: (previous, value) => {
      const campaign = Number(String(previous).replace('preferences:', ''));
      if (campaign) {
        sendKeepaliveSave(
          preferencesPath(campaign),
          preferencesBody(value),
          'POST',
          version.basedOn(campaign)
        );
      }
    },
  });

  /** One undo step puts a whole earlier screen back, and rebuilds the form. */
  const { canUndo, canRedo, undo, redo } = useUndoRedo<PreferencesWork>({
    value: work,
    enabled: own,
    identity: activeVacancy ? `${activeVacancy}:${loads}` : null,
    apply: (earlier) => {
      setFormData((current) =>
        current?.map((section) => ({
          ...section,
          questions: section.questions.map((question) =>
            question.key in earlier
              ? {
                  ...question,
                  value:
                    earlier[question.key as keyof PreferencesWork] ?? undefined,
                }
              : question
          ),
        }))
      );
      // The form is rebuilt from these values (its boxes start from them).
      setFormKey(Math.random());
    },
  });

  // Closing the tab or reloading while held back: nothing can be sent for a
  // screen that is not stored, so the browser asks first.
  useBeforeUnloadWarning(heldBack);

  // What the sidebar and the campaign picker ask before taking the person
  // away. A screen held back answers "not kept", with the reason.
  useEffect(() => {
    if (!own) return undefined;
    setPending(
      (hasPending && hasFailed) || heldBack,
      heldBack ? missing[0] : undefined
    );
    registerAbandon(abandon);
    registerSaveNow(
      () => (heldBackRef.current ? Promise.resolve(false) : saveNow()),
      () => !heldBackRef.current && isKept()
    );
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    own,
    hasPending,
    hasFailed,
    heldBack,
    missing[0],
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
  ]);

  const deleteVacancyHandler = useCallback(() => {
    if (loadingConfig || !activeVacancy) return;

    deleteVacancy({
      variables: {
        vacancyId: activeVacancy,
      },
    }).then(() => {
      addToast({
        type: ToastTypes.SUCCESS,
        description: 'Campaign successfully deleted',
      });
      unsetActiveVacancy();
    });
  }, [
    activeVacancy,
    deleteVacancy,
    navigate,
    loadingConfig,
    unsetActiveVacancy,
  ]);

  const deleteVacancyNow = useRef(deleteVacancyHandler);
  deleteVacancyNow.current = deleteVacancyHandler;

  const builtFor = useRef<number | null>(null);
  useEffect(() => {
    const config = configData?.preferencesConfig;
    /*
     * The form is built ONCE per campaign. A later answer for the same
     * campaign (after linking or unlinking an external job) does not rebuild
     * it: rebuilt from the server it threw away an emptied name and every
     * change not stored yet, without a word (review of the slice). The
     * linked jobs are drawn from the answer itself, further down.
     */
    if (config && activeVacancy && builtFor.current === activeVacancy) return;
    if (config) builtFor.current = activeVacancy ?? null;
    const data: PreferencesFormData[] = [
      {
        section: 'General',
        questions: [
          {
            ...QUESTIONS.name,
            value: config?.name,
          },
          {
            ...QUESTIONS.lead_recruiter,
            value: config?.lead_recruiter_id ?? 0,
          },
          {
            ...QUESTIONS.hide_from_careers_page,
            value: config?.excluded_from_careers_page ?? false,
          },
        ],
      },
      {
        section: 'Automated Candidate Mails',
        questions: [
          {
            ...QUESTIONS.vacancy_template_done_survey,
            value:
              config?.vacancyTemplates.done_survey === null
                ? 'null'
                : config?.vacancyTemplates.done_survey ?? undefined,
          },
          {
            ...QUESTIONS.vacancy_template_ask_for_cv,
            value:
              config?.vacancyTemplates.ask_for_cv === null
                ? 'null'
                : config?.vacancyTemplates.ask_for_cv ?? undefined,
          },
          {
            ...QUESTIONS.vacancy_template_got_cv,
            value:
              config?.vacancyTemplates.got_cv === null
                ? 'null'
                : config?.vacancyTemplates.got_cv ?? undefined,
          },
        ],
        content: isAllowedToCreateTemplates && (
          <PostHogFeature flag="templates-editor" match={true}>
            <div tw="mt-6">
              <Link
                to={Routes.TEMPLATE_SETTINGS}
                tw="flex flex-row items-center gap-1 text-sm font-medium text-indigo-800"
              >
                Create your own templates <ArrowRight />
              </Link>
            </div>
          </PostHogFeature>
        ),
      },
      {
        section: 'Notifications',
        questions: [
          {
            ...QUESTIONS.email_on_candidate,
            value: config?.notificationPreferences.email_on_candidate,
          },
          {
            ...QUESTIONS.email_on_communication,
            value: config?.notificationPreferences.email_on_communication,
          },
        ],
        content: (
          <div tw="mt-6">
            <Footnote>
              These notification settings apply to your personal account.
            </Footnote>
          </div>
        ),
      },
      {
        section: 'Other',
        questions: [
          {
            ...QUESTIONS.delete_vacancy,
            handler: () => deleteVacancyNow.current(),
          },
        ],
      },
    ];
    setFormData(data);
    setIsDoneUpdating(true);
    setFormKey(Math.random());
    // What was loaded is this campaign's; a fresh load is no undo step.
    if (config) {
      setLoadedFor(activeVacancy ?? null);
      setLoads((n) => n + 1);
    }
    /*
     * Built from what was LOADED, and only then. This used to run again
     * whenever the delete handler was made anew, which happens when things
     * above this screen draw again (the leave question, for one). The form
     * was then rebuilt from the loaded values under the person's hands: an
     * emptied name came straight back (seen in the first browser run of
     * this screen). With a Save button nobody noticed; a screen that stores
     * itself would have stored the old name as if it had been typed.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData?.preferencesConfig]);

  const handleLinkExternalJob = () => {
    linkExternalJob({
      variables: {
        input: {
          vacancy_id: activeVacancy,
          integration_id: externalJobs.find(
            (j: any) => j.id === selectedExternalJob
          )?.integration_id,
          external_job_id: selectedExternalJob,
        },
      },
    }).then(async () => {
      setSelectedExternalJob(undefined);
      // Loading the screen again rebuilds the form: first store what is on it.
      await saveNow();
      refetchConfigData();
    });
  };

  const handleUnlinkExternalJob: handleUnlinkExternalJobFn = (externalJob) => {
    unlinkExternalJob({
      variables: {
        input: {
          vacancy_id: activeVacancy,
          integration_id: externalJob.integration_id,
          external_job_id: externalJob.id,
        },
      },
    }).then(async () => {
      await saveNow();
      refetchConfigData();
    });
  };

  return (
    <AppPage
      heading="Campaign preferences"
      loading={!isDoneUpdating}
      cta={
        <div tw="flex items-center gap-5">
          <UndoRedo
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
          />
          <OutOfDateBox
            refusal={refusal}
            onLoadTheirs={() => {
              abandon();
              window.setTimeout(() => window.location.reload(), 100);
            }}
            onKeepMine={() => {
              version.keepMine();
              retryRefused();
            }}
          />
          <SaveStatus
            state={saveState}
            keptAt={keptAt}
            switching={switching}
            notSavedBecause={heldBack ? `Not saved: ${missing[0]}` : undefined}
          />
        </div>
      }
    >
      <FormProvider {...formMethods}>
        <ConfigForm
          key={formKey}
          formData={preferencesData}
          onChange={formDataChangeHandler}
        />
      </FormProvider>
      {userHasPermission(PERMISSIONS.MANAGE_CAMPAIGNS) && (
        <PostHogFeature flag="integrations" match={true}>
          <PreferencesIntegrations
            configData={configData}
            externalJobs={externalJobs}
            loadingExternalJobs={loadingExternalJobs}
            selectedExternalJob={selectedExternalJob}
            onHandleLinkExternalJob={handleLinkExternalJob}
            onSetSelectedExternalJob={setSelectedExternalJob}
            onRefetchExternalJobs={refetchExternalJobs}
            onHandleUnlinkExternalJob={handleUnlinkExternalJob}
            waitingCandidates={waitingData?.waitingCandidates}
          />
        </PostHogFeature>
      )}
    </AppPage>
  );
};

export default PreferencesConfig;
